import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, UsersThree, ArrowRight, Phone, EnvelopeSimple, Buildings, MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";

const empty = { name: "", contact_name: "", email: "", phone: "", sms_phone: "", states_covered: "", management_fee_pct: "" };

export default function PropertyManagers() {
  const [pms, setPms] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const navigate = useNavigate();

  const load = () => api.get("/property-managers").then((r) => setPms(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Company / manager name is required"); return; }
    try {
      await api.post("/property-managers", { ...form, management_fee_pct: form.management_fee_pct === "" || form.management_fee_pct == null ? null : Number(form.management_fee_pct) });
      toast.success("Property manager added");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error("Failed to add"); }
  };

  return (
    <div className="space-y-8 animate-fade-up">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Partners</p>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-1">Property Managers</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-pm-button" className="rounded-sm font-semibold transition-transform hover:-translate-y-[1px]">
              <Plus size={16} weight="bold" className="mr-1" /> Add Manager
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm">
            <DialogHeader><DialogTitle className="font-display text-2xl font-black">New Property Manager</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider font-bold">Company / Name</Label>
                <Input data-testid="pm-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Summit Property Mgmt" className="rounded-sm mt-1.5" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-bold">Primary Contact</Label>
                  <Input data-testid="pm-contact-input" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="rounded-sm mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-bold">States Covered</Label>
                  <Input data-testid="pm-states-input" value={form.states_covered} onChange={(e) => setForm({ ...form, states_covered: e.target.value })} placeholder="TX, UT" className="rounded-sm mt-1.5" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider font-bold">Email</Label>
                  <Input data-testid="pm-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider font-bold">Phone</Label>
                  <Input data-testid="pm-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm mt-1.5" />
                </div>
              </div>
            </div>
            <DialogFooter><Button data-testid="save-pm-button" onClick={submit} className="rounded-sm font-semibold w-full">Save Manager</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {pms.length === 0 ? (
        <div className="bg-card border border-border rounded-sm p-12 text-center">
          <UsersThree size={40} className="mx-auto text-muted-foreground mb-4" />
          <p className="font-display text-xl font-bold">No property managers yet</p>
          <p className="text-sm text-muted-foreground mt-1">Add the companies (or people) who manage your rentals to track contracts and communications.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pms.map((pm) => (
            <div key={pm.id} data-testid={`pm-card-${pm.id}`} onClick={() => navigate(`/property-managers/${pm.id}`)}
              className="bg-card border border-border rounded-sm p-5 cursor-pointer transition-all hover:shadow-md hover:-translate-y-[2px] group">
              <div className="flex items-start justify-between">
                <div className="h-10 w-10 bg-foreground text-background flex items-center justify-center rounded-sm font-display font-black">
                  {pm.name?.[0]?.toUpperCase()}
                </div>
                <ArrowRight size={18} className="text-muted-foreground group-hover:translate-x-1 transition-transform" />
              </div>
              <h3 className="font-display text-xl font-bold mt-4 tracking-tight">{pm.name}</h3>
              {pm.contact_name && <p className="text-sm text-muted-foreground">{pm.contact_name}</p>}
              <div className="mt-3 space-y-1 text-sm text-muted-foreground">
                {pm.email && <p className="flex items-center gap-2 truncate"><EnvelopeSimple size={14} /> {pm.email}</p>}
                {pm.phone && <p className="flex items-center gap-2"><Phone size={14} /> {pm.phone}</p>}
                {pm.states_covered && <p className="flex items-center gap-2"><MapPin size={14} /> {pm.states_covered}</p>}
              </div>
              <div className="mt-4 pt-4 border-t border-border flex items-center gap-2 text-sm font-semibold">
                <Buildings size={15} /> {pm.property_count} {pm.property_count === 1 ? "property" : "properties"}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
